import React from 'react';

import { Text } from '@mantine/core';
import Table from 'components/Table';
import usePackageUtilization from 'hooks/reports/utilization/package-utilization/usePackageUtilization';
import { useNavigate } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import { PackageUtilizationReport } from 'types';

import CalculatedExpectationCell from './cells/CalculatedExpectationCell';
import ExpectationCell from './cells/ExpectationCell';
import UtilizationRateCell from './cells/UtilizationRateCell';
import UtilizationRateHeader from './headers/UtilizationRateHeader';
import useOrdering from './ordering';
import { useSearchFilters } from '../../../SearchFiltersContext';

const PackageUtilizationTable = (): JSX.Element => {
  const { searchFilters } = useSearchFilters();
  const navigate = useNavigate();
  const { data, isError, isLoading, isSuccess } = usePackageUtilization({
    period: searchFilters.period,
    startDate: searchFilters.startDate,
    endDate: searchFilters.endDate,
    athleteIds: searchFilters.athleteIds,
    packageIds: searchFilters.packageIds,
    eventTypeIds: searchFilters.eventTypes.map(eventType => eventType.eventTypeId),
    utilizationRateRange: searchFilters.utilizationRateRange,
    groupByAthlete: searchFilters.groupByAthlete,
    groupByPackage: searchFilters.groupByPackage,
  });

  const { ordering, ascending, handleHeaderClick, sortedData } = useOrdering({ data });

  const columns = [
    {
      Header: 'Athlete Name',
      accessor: 'athleteFullName' as const,
      Cell: ({ row }: CellProps<PackageUtilizationReport>) => (
        <Text
          fw='bold'
          fs='italic'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => navigate(`/users/athletes/${row.original.athleteId}/general-info`)}
        >
          {row.original.athleteFullName}
        </Text>
      ),
    },
    {
      Header: 'Package',
      accessor: 'packageName' as const,
    },
    {
      Header: 'Class Attendance',
      accessor: 'classAttendance' as const,
    },
    {
      Header: 'Appointment Attendance',
      accessor: 'appointmentAttendance' as const,
    },
    {
      Header: 'Total Attendance',
      accessor: 'totalAttendance' as const,
    },
    {
      Header: 'Days Attended',
      accessor: 'daysAttended' as const,
    },
    {
      id: 'expectation',
      Header: 'Expectation',
      Cell: ({ row: { original } }: CellProps<PackageUtilizationReport>) => (
        <ExpectationCell packageUtilizationReport={original} />
      ),
    },
    {
      Header: 'Calc. Expectation',
      accessor: 'calculatedExpectation' as const,
      Cell: ({ value }: CellProps<PackageUtilizationReport>) => <CalculatedExpectationCell value={value} />,
    },
    {
      Header: <UtilizationRateHeader />,
      accessor: 'utilizationRate' as const,
      Cell: ({ value }: CellProps<PackageUtilizationReport>) => <UtilizationRateCell value={value} />,
    },
  ] satisfies Column<PackageUtilizationReport>[];
  return (
    <>
      <Table
        tableData={sortedData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={false}
        hasNextPage={false}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
        clickableHeaders={[
          'athleteFullName',
          'packageName',
          'classAttendance',
          'appointmentAttendance',
          'totalAttendance',
          'daysAttended',
          'expectedFrequencyValue',
          'expectedFrequencyUnit',
          'expectation',
          'calculatedExpectation',
          'utilizationRate',
        ]}
      />
    </>
  );
};

export default PackageUtilizationTable;
